import { Component, OnInit } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { DatePickerHandler } from '@common/UI/date-picker/date-picker.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { commit, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { DataService, DateRange, ProductUnit } from '@core/services/data.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { Multilingual } from '@core/utilities/multilingual';
import { Storage } from '@core/utilities/storage';
import { map } from 'rxjs/operators';
import { Report } from '../common/report';
import { ExportsViewerComponent } from './exports-viewer/exports-viewer.component';
import { ImportsViewerComponent } from './imports-viewer/imports-viewer.component';
import { InitialViewerComponent } from './initial-viewer/initial-viewer.component';
import { RemainingViewerComponent } from './remaining-viewer/remaining-viewer.component';
import { ReturnsViewerComponent } from './returns-viewer/returns-viewer.component';
import { Observable } from 'rxjs';

@Component({
  templateUrl: './inventory-report-by-products.component.html',
  styleUrls: ['./inventory-report-by-products.component.scss']
})
export class InventoryReportByProductsComponent implements OnInit {

  public get mode(): 'quantity' | 'worth' {
    return Storage.get(location.pathname, 'quantity');
  }

  public set mode(value: 'quantity' | 'worth') {
    Storage.set(location.pathname, value);
  }

  public paths: Report[] = [];
  public report: Report;

  constructor(private ds: DataService, private layout: LayoutService, private sync: SynchronizationService, private modal: ModalService) {
  }

  public get loading(): boolean {
    return this.range && !this.sync.ready(InventoryReportByProductsComponent);
  }

  public range: { name: string, range: DateRange } = null;

  public goods = new Filterable<Report>({
    extractor: i => [i.Path.join('.')],
    grouper: i => [
      { name: 'Theo trạng thái nhập', option: i.Imports.LengthCount > 0 ? 'Có nhập' : 'Không nhập' },
      { name: 'Theo trạng thái bán', option: i.Exports.LengthCount > 0 ? 'Có bán' : 'Không bán' },
      { name: 'Theo trạng thái trả', option: i.Returns.LengthCount > 0 ? 'Có trả' : 'Không trả' },
      { name: 'Theo trạng thái mất', option: i.CompanyLosses.LengthCount > 0 ? 'Có mất' : 'Không mất' },
      { name: 'Theo trạng thái tồn', option: 1 > 0 ? 'Có tồn' : 'Hết hàng' },
    ]
  });

  public selectMode() {
    this.modal.show(MessageBoxComponent, {
      state: {
        title: Multilingual.getText('Chọn hình thức hiển thị'),
        message: Multilingual.getText('Chọn một trong các hình thức hiển thị dữ liệu dưới đây') + ':',
        buttons: [
          { name: Multilingual.getText('Hiển thị theo số lượng hàng'), class: this.mode == 'quantity' ? '' : 'bg-primary text-white', click: () => this.mode = 'quantity', icon: this.mode == 'quantity' ? 'fa fa-check' : 'fa fa-boxes' },
          { name: Multilingual.getText('Hiện thị theo giá trị hàng'), class: this.mode == 'worth' ? '' : 'bg-primary text-white', click: () => this.mode = 'worth', icon: this.mode == 'worth' ? 'fa fa-check' : 'fa fa-coins' }
        ]
      }
    });
  }

  public extractor(field: string): SortableExtractor<Report> {
    return {
      extract: i => {
        switch (field) {
          case 'last-of-path': return i.Path.last();
          case 'initial': return i.base('initial', 'CNY');
          case 'initial-quantity': return i.Initial.LengthQuantity + i.Initial.WeightQuantity;
          case 'import': return i.base('import', 'CNY') - i.base('provider', 'CNY');
          case 'import-quantity': return i.Imports.LengthQuantity - i.ProviderLosses.LengthQuantity + i.Imports.WeightQuantity - i.ProviderLosses.WeightQuantity;
          case 'return': return i.quote('return', 'VND');
          case 'return-quantity': return i.Returns.LengthQuantity + i.Returns.WeightQuantity;
          case 'discount': return i.quote('discount', 'VND');
          case 'discount-quantity': return i.Exports.LengthDiscount + i.Exports.WeightDiscount;
          case 'lost': return i.quote('lost', 'VND');
          case 'lost-quantity': return i.CompanyLosses.LengthQuantity + i.CompanyLosses.WeightQuantity;
          case 'export': return i.quote('export', 'VND');
          case 'export-quantity': return i.Exports.LengthQuantity + i.Exports.WeightQuantity;
          case 'left': return i.base('left', 'CNY');
          case 'left-quantity': return i.left('quantity');
          case 'exchange': return i.exchange;
          case 'gross': return i.gross('total', 'VND');
          case '%': return i.base('profitable', 'CNY') != 0 ? i.gross('total', 'CNY') * 100 / i.base('profitable', 'CNY') : 0
        }
      }
    };
  }

  private synchronize(observable: boolean): Observable<boolean> {

    if (observable) {

      this.sync.notify(InventoryReportByProductsComponent, false);

      return this.ds.reports('goods-by-products', { range: this.range.range }).pipe(map(data => {

        this.sync.notify(InventoryReportByProductsComponent, true);

        if (data && data.Goods) {

          Report.reset(Multilingual.getText('Tất cả mã hàng'));
          data.Goods.forEach(i => Report.add(i));

          this.paths = [];
          this.open(Report.root);

          return true;
        }

        return false;

      }));
    }
    else {

      this.sync.with(InventoryReportByProductsComponent).repeat(() => this.ds.reports('goods-by-products', { range: this.range.range }).pipe(map(data => {

        this.sync.notify(InventoryReportByProductsComponent, true);

        if (data && data.Goods) {

          Report.reset(Multilingual.getText('Tất cả mã hàng'));
          data.Goods.forEach(i => Report.add(i));

          this.paths = [];
          this.open(Report.root);

          return true;
        }

        return false;

      })));

      return null;
    }
  }

  public handler: DatePickerHandler = {
    change: (info: { name: string, range: DateRange }) => {

      this.range = info;
      this.goods.source = [];

      return this.synchronize(true);
    }
  }

  public get primaryLabel(): string {
    switch (this.paths.indexOf(this.report)) {
      case 0: return Multilingual.getText('Mã hàng');
      case 1: return Multilingual.getText('Mã màu');
    }
  }


  public openStatistics(report: Report, type: 'imports' | 'exports' | 'initial' | 'remaining' | 'returns') {

    this.sync.with(InventoryReportByProductsComponent).commit(done => {

      switch (type) {
        case 'initial':

          this.ds.reports('goods-by-imports', { range: this.range.range, sku: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined }).subscribe(response => {
            done();
            this.modal.show(InitialViewerComponent, { state: { range: this.range, reports: response.Goods, SKU: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined } }).content.changed.subscribe(invoice => this.synchronize(false));
          });
          break;

        case 'remaining':

          this.ds.reports('goods-by-imports', { range: this.range.range, sku: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined }).subscribe(response => {
            done();
            this.modal.show(RemainingViewerComponent, { state: { range: this.range, reports: response.Goods, SKU: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined } }).content.changed.subscribe(invoice => this.synchronize(false));
          });
          break;

        case 'imports':

          this.ds.reports('goods-by-imports', { range: this.range.range, sku: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined }).subscribe(response => {
            done();
            this.modal.show(ImportsViewerComponent, { state: { range: this.range, reports: response.Goods, SKU: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined } }).content.changed.subscribe(invoice => this.synchronize(false));
          });
          break;

        case 'exports':

          this.ds.reports('goods-by-exports', { range: this.range.range, sku: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined }).subscribe(response => {
            done();
            this.modal.show(ExportsViewerComponent, { state: { range: this.range, reports: response.Goods, SKU: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined } });
          });
          break;

        case 'returns':

          this.ds.reports('goods-by-returns', { range: this.range.range, sku: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined }).subscribe(response => {
            done();
            this.modal.show(ReturnsViewerComponent, { state: { range: this.range, reports: response.Goods, SKU: report.Path[0], color: report.Path.length > 1 ? report.Path[1] : undefined } });
          });
          break;

        default:

          done();
          break;
      }
    });    
  }

  public statistics(field: 'left' | 'gross-value' | 'gross-percentage' | 'discount' | 'exchange' | 'return' | 'export' | 'import'): number {
    switch (field) {
      case 'gross-value': return this.goods.matches.sum(i => i.gross('total', 'VND'));
      case 'gross-percentage': return this.goods.matches.sum(i => i.gross('total', 'CNY')) / this.goods.matches.sum(i => i.base('profitable', 'CNY'));
      case 'import': return this.goods.matches.sum(i => this.mode == 'worth' ? i.quote('import', 'CNY') - i.quote('provider', 'CNY') : i.Imports.LengthQuantity - i.ProviderLosses.LengthQuantity);
      case 'exchange':

        let cny = this.goods.matches.sum(i => i.gross('total', 'CNY'));
        return cny != 0 ? this.statistics('gross-value') / cny : 0;

      case 'left': return this.goods.matches.sum(i => i.base('left', 'CNY'));

      default: return this.goods.matches.sum(i => i.quote(field as any, 'VND'));
    }
  }

  public sum(type: string, field: string = 'Quantity') {
    switch (type) {
      case 'left': return this.goods.matches.sum(i => i.left(field.toLowerCase() as any));
      default: return this.goods.matches.sum(i => i[type][field]);
    }
    
  }

  public open(report: Report) {

    if (report.children.length < 1) return;

    if (!this.paths.contains(report)) this.paths.push(report);
    this.paths = this.paths.take(this.paths.indexOf(report) + 1);
    this.report = report;

    this.goods.source = report.children;
    this.goods.search = '';
  }

  ngOnInit(): void {
    main(() => this.layout.title = Multilingual.getText('Báo cáo hàng hóa theo mã hàng'));
  }

}
